/**
 * Date Formatting Utilities
 *
 * Provides consistent date formatting across the application.
 * All functions accept either a Date object or an ISO date string
 * (e.g., "2024-01-15T10:30:00Z") and return a human-readable string
 * formatted for the en-US locale.
 */

/**
 * Formats a date into a short, human-readable string.
 *
 * @param date - A Date object or ISO 8601 date string from the API
 * @returns A formatted date string like "Jan 15, 2024"
 *
 * @example
 * formatDate("2024-01-15T10:30:00Z") // "Jan 15, 2024"
 * formatDate(new Date())			   // "Mar 27, 2026"
 */
export function formatDate(date: Date | string | undefined | null): string {
	// Guard against undefined/null values from incomplete DB records
	if (!date) return '—';
	// Normalize input: convert string dates (from API responses) to Date objects
	const d = typeof date === 'string' ? new Date(date) : date;
	return d.toLocaleDateString('en-US', {
		year: 'numeric',
		month: 'short',
		day: 'numeric',
	});
}

/**
 * Formats a calendar day — a `yyyy-mm-dd` date column such as a work order's
 * scheduled date or a recurring series' next job date — in the same
 * "Jan 15, 2024" style as formatDate.
 *
 * Why not formatDate: `new Date("2024-01-15")` is read as midnight UTC, which
 * west of Greenwich is the evening of the 14th, so the day shown slips back by
 * one. This reads only the first ten characters (the API sometimes appends a
 * time) and builds the date in local time, so the calendar day never moves.
 *
 * Replaces six hand-rolled copies of
 * `new Date(`${d.slice(0, 10)}T00:00:00`).toLocaleDateString()`.
 *
 * @param date - A `yyyy-mm-dd` string (optionally with a time part), or null.
 * @returns e.g. "Jan 15, 2024", or "—" when there is no date.
 *
 * @example
 * formatDay("2026-11-01")                 // "Nov 1, 2026"
 * formatDay("2026-11-01T00:00:00.000000Z") // "Nov 1, 2026"
 */
export function formatDay(date: string | undefined | null): string {
	if (!date) return '—';
	return formatDate(new Date(`${String(date).slice(0, 10)}T00:00:00`));
}

/**
 * Returns an ISO `yyyy-mm-dd` date string for `days` days before today,
 * computed in local time (so it lines up with the native <input type="date">
 * controls in the Filter Control, which also operate in local time).
 *
 * Used to seed the list filters' default "look back N days" timeframe — e.g.
 * `isoDaysAgo(14)` gives the start of the default 2-week window.
 *
 * @param days - How many days to subtract from today (0 = today).
 * @returns A `yyyy-mm-dd` string suitable for a date input's value.
 *
 * @example
 * isoDaysAgo(14) // "2026-05-24" when today is 2026-06-07
 */
export function isoDaysAgo(days: number): string {
	const d = new Date();
	d.setDate(d.getDate() - days);
	// Build the ISO date from local parts — toISOString() would shift by the
	// timezone offset and can roll the date back a day in western zones.
	const year = d.getFullYear();
	const month = String(d.getMonth() + 1).padStart(2, '0');
	const day = String(d.getDate()).padStart(2, '0');
	return `${year}-${month}-${day}`;
}

/**
 * UTC counterpart of isoDaysAgo. Use this when the string will be compared
 * against server-stamped DATE columns (e.g. completion_date), which the
 * backend writes from UTC "today" — a local-calendar window can miss records
 * stamped with tomorrow's UTC date during the evening hours.
 *
 * @param days - How many days to subtract from the current UTC date (0 = today).
 * @returns A `yyyy-mm-dd` string in UTC.
 */
export function isoUtcDaysAgo(days: number): string {
	const d = new Date();
	d.setUTCDate(d.getUTCDate() - days);
	return d.toISOString().slice(0, 10);
}

/**
 * Formats a date into a string that includes both date and time.
 *
 * @param date - A Date object or ISO 8601 date string from the API
 * @returns A formatted datetime string like "Jan 15, 2024, 10:30 AM"
 *
 * @example
 * formatDateTime("2024-01-15T10:30:00Z") // "Jan 15, 2024, 10:30 AM"
 */
export function formatDateTime(date: Date | string | undefined | null): string {
	// Guard against undefined/null values from incomplete DB records
	if (!date) return '—';
	// Normalize input: convert string dates (from API responses) to Date objects
	const d = typeof date === 'string' ? new Date(date) : date;
	return d.toLocaleString('en-US', {
		year: 'numeric',
		month: 'short',
		day: 'numeric',
		hour: 'numeric',
		minute: '2-digit',
	});
}

/**
 * Formats a date as a relative time string (e.g., "5m ago", "2d ago").
 * Falls back to an absolute date (via formatDate) for anything older than 7 days.
 *
 * This is used in activity feeds, notifications, and list views where
 * recency matters more than the exact timestamp.
 *
 * @param date - A Date object or ISO 8601 date string from the API
 * @returns A relative time string like "just now", "5m ago", "3h ago", "2d ago",
 *			or a formatted date for anything older than 7 days
 *
 * @example
 * formatRelative(new Date())							// "just now"
 * formatRelative("2024-01-15T10:30:00Z")				// "Jan 15, 2024"
 */
export function formatRelative(date: Date | string | undefined | null): string {
	// Guard against undefined/null values from incomplete DB records
	if (!date) return '—';
	// Normalize input: convert string dates (from API responses) to Date objects
	const d = typeof date === 'string' ? new Date(date) : date;
	const now = new Date();

	// Calculate the difference in progressively larger time units
	const diffMs = now.getTime() - d.getTime();
	const diffSeconds = Math.floor(diffMs / 1000);
	const diffMinutes = Math.floor(diffSeconds / 60);
	const diffHours = Math.floor(diffMinutes / 60);
	const diffDays = Math.floor(diffHours / 24);

	// Return the most appropriate human-readable relative time
	if (diffSeconds < 60) return 'just now';
	if (diffMinutes < 60) return `${diffMinutes}m ago`;
	if (diffHours < 24) return `${diffHours}h ago`;
	if (diffDays < 7) return `${diffDays}d ago`;

	// For dates older than a week, show the full formatted date instead
	return formatDate(d);
}

/**
 * Formats a "who did this, and when" attribution timestamp shown next to a
 * person's name on completed checklist items (e.g. "Mon, 01/15/2024, 3:30 PM EST").
 *
 * Uses the browser's default locale ([]) and includes the short time-zone name so
 * field techs across regions can tell exactly when an item was completed. This is
 * the single source of truth for the attribution format that the WorkOrder and
 * Inspection checklist organisms previously each re-declared inline.
 *
 * @param date - A Date object or ISO 8601 date string
 * @returns A formatted attribution string
 */
export function formatAttribution(date: Date | string): string {
	const d = typeof date === 'string' ? new Date(date) : date;
	return d.toLocaleString([], {
		weekday: 'short',
		month: '2-digit',
		day: '2-digit',
		year: 'numeric',
		hour: 'numeric',
		minute: '2-digit',
		hour12: true,
		timeZoneName: 'short',
	});
}

/**
 * Formats the compact "submitted at" timestamp shown under a sign-off signature
 * (e.g. "Jan 15, 3:30 PM"). Shared by the WorkOrder and Inspection sign-off steps.
 *
 * @param date - A Date object or ISO 8601 date string
 * @returns A formatted short timestamp string
 */
export function formatSignOffTimestamp(date: Date | string): string {
	const d = typeof date === 'string' ? new Date(date) : date;
	return d.toLocaleString([], {
		month: 'short',
		day: 'numeric',
		hour: 'numeric',
		minute: '2-digit',
	});
}
